export enum ProductTier {
  CLASSIQUE = 'Classique',
  COUTURE = 'Couture',
  PREMIER = 'Premier',
  CLASSIQUE_PLUS = 'Classique+',
  COUTURE_PLUS = 'Couture+'
}

export enum ProductTierSlug {
  CLASSIQUE = 'classique',
  COUTURE = 'couture',
  PREMIER = 'premier',
  CLASSIQUE_PLUS = 'classique-plus',
  COUTURE_PLUS = 'couture-plus'
}

export enum InsuranceLevel {
  STANDARD = 'Standard',
  PREMIUM = 'Premium'
}

export enum InsuranceLevelSlug {
  STANDARD = 'standard',
  PREMIUM = 'premium'
}

export enum ProductStore {
  REVOLVE = 'Revolve',
  FWRD = 'FWRD',
  VIVRELLE = 'vivrelle'
}

export type ProductCardData = {
  id: string;
  borrowed: boolean;
  brand: string;
  name: string;
  slug: string;
  sku: string;
  tier: string;
  mainImage: string;
  item_group_id: string; // This is the SKU
  hoverImage?: string;
  price?: number;
  link?: string;
  color?: string[];
  category?: 'handbag' | 'accessory' | string;
  store?: 'Revolve' | 'revolve' | 'FWRD' | 'vivrelle';
  cj_link?: string;
  size?: string;
  taxItemId?: string;
  showWishlist?: boolean;
};

type RawEPCCProductFileData = {
  created_at: string;
  id: string;
  type: string;
};

export type RawEPCCImageSet = {
  main: string;
  hover: string;
  list: string[];
};

// PARTIAL TYPE.
// Only identifying the values we need for ProductDetailData and a few other
// that I think are going to be useful
export type RawEPCCProduct = {
  id: string;
  attributes: {
    created_at: string;
    description: string;
    extensions: {
      'products(product-attributes)': {
        brand: string;
        color: string;
        /**
         * @deprecated
         * Use /v2/inventory/:productId endpoint to determine product
         * availability instead
         */
        'is-available': boolean;
        materials: string[];
        occasion: string[];
        'product-condition': string;
        'product-notes': string;
        'product-size': string;
        'subscription-level': string;
        /**
         * WARNING: This value can come in as a JSON string. Use `typeof value === 'string'` to determine,
         * then use JSON.parse() and cast to `RawEPCCImageSet`
         */
        images: string | RawEPCCImageSet;
      };
    };
    name: string;
    price: {
      USD: {
        amount: number;
        includes_tax: boolean;
      };
    };
    sku: string;
    slug: string;
    status: 'draft' | 'live';
    updated_at: string;
    published_at: string;
  };
  meta: {
    catalog_id: string;
    bread_crumb_nodes: string[];
    display_price: {
      without_tax: {
        amount: number;
        currency: 'USD';
        formatted: string;
      };
    };
    /** If this exists, the product is sized */
    variation_matrix?: {
      /**
       * The KEY is the productId of the parent product.
       * The VALUE is the productId of the item to add to the cart
       */
      [parentUUID: string]: string;
    };
    variations?: {
      id: string;
      name: 'size';
      options: {
        id: string;
        description: string;
        name: string;
      }[];
    }[];
  };
  relationships: {
    files: {
      data: RawEPCCProductFileData[];
    };
    main_image: {
      data: {
        id: string;
        type: 'main_image';
      };
    };
  };
};

// PARTIAL TYPE. Only notating the important stuff
export type RawEPCCFile = {
  data: {
    link: {
      href: string;
    };
  };
};

export type ProductBreadcrumb = {
  displayText: string;
  url: string;
};

export type ProductSizeVariant = {
  addToCartUUID: string;
  sizeDescription: string;
  productVariantId: string;
};

export type ProductDetailData = {
  id: string;
  brand: string;
  slug: string;
  sku: string;
  productName: string;
  productTier: string;
  estimatedRetailValue: string;
  descriptionHTML: string;
  sizeHTML: string;
  conditionHTML: string;
  breadcrumbs: ProductBreadcrumb[];
  images: string[];
  borrowed?: boolean;
  sizeVariants?: ProductSizeVariant[];
  color: string[];
};

export type ProductAvailabilityResponse = {
  success: boolean;
  available: boolean;
};
